import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DinoId, DINOS, DINO_ORDER } from '../data/dinos';
import { Dino } from '../objects/Dino';
import { Alien } from '../objects/Enemy';
import { Bullet } from '../objects/Bullet';
import { Ufo } from '../objects/Ufo';
import { BossAsteroid } from '../objects/BossAsteroid';
import { PowerUp, PowerUpKind } from '../objects/PowerUp';
import { TouchControls, isTouchDevice } from '../objects/TouchControls';
import { Hud } from '../objects/Hud';
import { Sfx } from '../objects/Sfx';
import { Music } from '../objects/Music';
import { CommsBox, COMMS, resetCommsDecks } from '../objects/CommsBox';

const GROUND_Y = GAME_HEIGHT - 24; // top of the ground row
const TILE = 16;
const WORLD_WIDTH = 8000; // big enough for a long run

export type GameMode = 'practice' | 'gauntlet';

interface GameInit {
  dinoId: DinoId;
  mode: GameMode;
  /** score carried from earlier gauntlet segments (0 in practice or the first leg) */
  runScore: number;
  /** Wall-clock timestamp (Date.now()) when the gauntlet's first leg started.
   *  Used at the finale to compute total run duration for the leaderboard.
   *  Practice runs and the first gauntlet leg leave this undefined; the
   *  scene then sets it itself on first creation if mode === 'gauntlet'. */
  runStartMs?: number;
}

export class GameScene extends Phaser.Scene {
  private dino!: Dino;
  private dinoId!: DinoId;
  private mode: GameMode = 'practice';
  private startingScore = 0;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private shootKey!: Phaser.Input.Keyboard.Key;
  /** Right Ctrl — alt jump key, tracked manually because Phaser's
   *  KeyCodes don't distinguish left vs right modifier keys (both share
   *  keyCode 17). We listen for `e.code === 'ControlRight'` at the
   *  window level instead, and store the held / just-pressed state here. */
  private rCtrlHeld = false;
  private rCtrlJustPressed = false;
  private rCtrlListener: ((e: KeyboardEvent) => void) | null = null;
  private rCtrlReleaseListener: ((e: KeyboardEvent) => void) | null = null;
  private touch!: TouchControls;
  private ground!: Phaser.Physics.Arcade.StaticGroup;
  private aliens!: Phaser.Physics.Arcade.Group;
  private ufos!: Phaser.Physics.Arcade.Group;
  private bullets!: Phaser.Physics.Arcade.Group;
  private spikes!: Phaser.Physics.Arcade.StaticGroup;
  private stars!: Phaser.Physics.Arcade.Group;
  private bigStars!: Phaser.Physics.Arcade.Group;
  private hearts!: Phaser.Physics.Arcade.Group;
  private hud!: Hud;
  private bgFar!: Phaser.GameObjects.TileSprite;
  private bgNear!: Phaser.GameObjects.TileSprite;
  private starField!: Phaser.GameObjects.Group;
  private ufoSpawnTimer = 0;
  private ufoSpawnInterval = 5000;
  private hasShownStarHint = false;
  private comms!: CommsBox;
  private nextEncouragementScore = 500;

  // Combo system — chain stomps within COMBO_WINDOW for a score multiplier.
  private comboCount = 0;
  private comboLastAt = 0;
  private static readonly COMBO_WINDOW = 1500;

  // Boss state.
  private bossActive = false;
  private currentBoss: BossAsteroid | null = null;
  private bossesCleared = new Set<number>();
  private arenaCenterX = 0;
  private arenaHalfWidth = 200;
  private bossHpBg!: Phaser.GameObjects.Rectangle;
  private bossHpFill!: Phaser.GameObjects.Rectangle;
  private bossHpLabel!: Phaser.GameObjects.Text;

  // Per-planet hazards.
  /** Lava — Inferno only, instakill on touch. */
  private lavaTiles!: Phaser.Physics.Arcade.StaticGroup;
  /** Falling icicles — Glacier only, drop from above on a timer. */
  private icicles!: Phaser.Physics.Arcade.Group;
  /** Bouncy mushroom platforms — Canopy only, super-launch the dino. */
  private bounceTiles!: Phaser.Physics.Arcade.StaticGroup;
  /** Active power-up drops floating in the world. */
  private powerUps!: Phaser.Physics.Arcade.Group;

  private score = 0;
  private wave = 1;
  private waveProgress = 0; // distance toward next wave
  private spawnTimer = 0;
  private spawnInterval = 1800; // ms — drops as waves climb
  /** Furthest right the dino has reached this run. Wave progression keys
   *  off this rather than current x so backtracking can't undo a wave. */
  private maxDinoX = 0;
  /** Wall-clock ms since this scene started — kept for telemetry / debug.
   *  No longer drives waves directly; idle-aware `idleMs` does that. */
  private sceneElapsedMs = 0;
  /** Continuous idle time — milliseconds since the dino last moved
   *  horizontally. Resets to 0 the instant they move. Active players
   *  see this stay near 0 and never trigger time-based waves; only true
   *  AFKers accumulate enough to force a wave. */
  private idleMs = 0;
  /** Previous frame's dino x — for detecting horizontal movement. */
  private prevDinoX = 0;
  /** Bottom-of-screen "← → MOVE  SPACE JUMP …" hint. Tracked so we can
   *  hide it during the pause overlay (its quit hint sits at the same y
   *  and the two overlap into an unreadable jumble otherwise). */
  private controlsHint: Phaser.GameObjects.Text | null = null;
  /** Set on the first leg of a gauntlet run; threaded through every later
   *  scene so the finale can compute total time. Undefined for practice. */
  private runStartMs?: number;

  constructor() {
    super('Game');
  }

  init(data: GameInit) {
    this.dinoId = data.dinoId ?? 'doux';
    this.mode = data.mode ?? 'practice';
    this.startingScore = data.runScore ?? 0;
    // Reset per-scene state explicitly. Phaser reuses scene instances on
    // restart so field initializers don't re-run; without this the wave
    // counters stick at their final values from the previous run.
    this.score = 0;
    this.wave = 1;
    this.waveProgress = 0;
    this.spawnTimer = 0;
    this.maxDinoX = 0;
    this.sceneElapsedMs = 0;
    this.idleMs = 0;
    this.prevDinoX = 0;
    // Stamp the start of a gauntlet run on the very first leg. Subsequent
    // legs carry the same timestamp through the scene transitions.
    if (this.mode === 'gauntlet') {
      this.runStartMs = data.runStartMs ?? Date.now();
    } else {
      this.runStartMs = undefined;
    }
  }

  create() {
    try {
      this.realCreate();
    } catch (e) {
      const err = e as Error;
      // Surface the failure to the page + console so we can see it. Without
      // this, an exception silently aborts the scene and leaves the player
      // stuck on the previous screen.
      // eslint-disable-next-line no-console
      console.error('GameScene.create failed:', err);
      const txt = `GameScene crashed:\n${err.message}\n${err.stack?.split('\n').slice(0, 5).join('\n')}`;
      this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, txt, {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: '#ff6a6a',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 20 },
        })
        .setOrigin(0.5);
    }
  }

  /** Real scene setup — wrapped in try/catch by create(). */
  private realCreate() {
    this.score = this.startingScore;
    this.wave = 1;
    // Tard's Dunes is action-light by nature (slower dino, fewer hazards),
    // so we crank the alien spawn rate to keep things busy.
    this.spawnInterval = this.dinoId === 'tard' ? 1300 : 1800;
    this.spawnTimer = 0;
    this.ufoSpawnInterval = 5000;
    this.ufoSpawnTimer = 0;
    this.hasShownStarHint = false;
    this.bossActive = false;
    this.currentBoss = null;
    this.bossesCleared = new Set();
    this.comboCount = 0;
    this.comboLastAt = 0;
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, GAME_HEIGHT);

    // Background — strongly tinted toward this planet's mood. Each planet
    // has a distinct top→bottom gradient so the moment you load in you feel
    // "OK, I'm on Inferno now" not "still in space."
    const bgTints: Record<DinoId, { top: number; mid: number; bottom: number }> = {
      doux: { top: 0x06122a, mid: 0x0a3a6e, bottom: 0x4080c0 }, // Glacier — icy gradient
      mort: { top: 0x1a0408, mid: 0x4a0a10, bottom: 0xa0301a }, // Inferno — magma red
      tard: { top: 0x1a0e04, mid: 0x4a2a10, bottom: 0xc08040 }, // Dunes — golden sand
      vita: { top: 0x06140a, mid: 0x144a28, bottom: 0x60a040 }, // Canopy — jungle green
    };
    const tint = bgTints[this.dinoId];
    this.cameras.main.setBackgroundColor(tint.top);

    // Vertical gradient backdrop covering the whole screen.
    const bgGradient = this.add.graphics().setScrollFactor(0).setDepth(-10);
    bgGradient.fillGradientStyle(tint.top, tint.top, tint.mid, tint.mid, 1, 1, 1, 1);
    bgGradient.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT - 60);
    bgGradient.fillGradientStyle(tint.mid, tint.mid, tint.bottom, tint.bottom, 1, 1, 1, 1);
    bgGradient.fillRect(0, GAME_HEIGHT - 60, GAME_WIDTH, 60);

    // Star/dust layer biased toward the planet stripe color.
    this.starField = this.add.group();
    const accentTint = DINOS[this.dinoId].stripe;
    for (let i = 0; i < 80; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, GROUND_Y - 16);
      const s = this.add
        .image(x, y, 'px')
        .setScrollFactor(0.2 + Math.random() * 0.4)
        .setAlpha(Phaser.Math.FloatBetween(0.4, 1))
        .setDepth(-5)
        .setTint(Math.random() < 0.25 ? accentTint : 0xffffff);
      this.starField.add(s);
    }

    // Strong horizon glow that connects the bg gradient to the ground.
    const horizonGfx = this.add.graphics().setScrollFactor(0).setDepth(-2);
    horizonGfx.fillGradientStyle(
      DINOS[this.dinoId].accent,
      DINOS[this.dinoId].accent,
      DINOS[this.dinoId].stripe,
      DINOS[this.dinoId].stripe,
      0,
      0,
      0.5,
      0.5,
    );
    horizonGfx.fillRect(0, GROUND_Y - 80, GAME_WIDTH, 80);
    void horizonGfx;
    // Unused parallax holders, kept for type compatibility.
    this.bgFar = this.add.tileSprite(-100, -100, 1, 1, 'px').setVisible(false);
    this.bgNear = this.add.tileSprite(-100, -100, 1, 1, 'px').setVisible(false);

    // Per-planet terrain.
    this.ground = this.physics.add.staticGroup();
    this.spikes = this.physics.add.staticGroup();
    this.lavaTiles = this.physics.add.staticGroup();
    this.icicles = this.physics.add.group();
    this.bounceTiles = this.physics.add.staticGroup();
    this.powerUps = this.physics.add.group();
    // Pickup groups must exist before buildPlanetTerrain — sky towers spawn
    // big stars during terrain generation, so the group needs to be ready.
    this.stars = this.physics.add.group({ allowGravity: false });
    this.bigStars = this.physics.add.group({ allowGravity: false });
    this.hearts = this.physics.add.group({ allowGravity: false });
    this.buildPlanetTerrain();

    // Pickups. Each pickup is clamped to a height the *playing* dino can
    // actually reach with a full jump from the ground — Mort (apex 62px)
    // shouldn't be looking up at stars Vita (apex 96px) could grab.
    // (Groups themselves were created earlier so terrain generators could
    // populate sky-tower big stars; here we just add ground-level pickups.)
    // Apex per dino (matches their jumpVelocity² / (2 * gravity)). Buffer of
    // 6px under apex so the dino actually overlaps the pickup at the top.
    const apexByDino: Record<DinoId, number> = {
      doux: 67, // -400 / 1100 → 73, minus 6 buffer
      mort: 56, // -370 → 62
      tard: 63, // -390 → 69
      vita: 90, // -460 → 96
    };
    const reachable = apexByDino[this.dinoId] ?? 60;
    const isOverHazard = (px: number) => {
      // Skip pickups directly above a lava tile or in a sky gap so the
      // player isn't punished for chasing them.
      const lavaUnder = (this.lavaTiles.getChildren() as Phaser.GameObjects.GameObject[]).some(
        (t) => {
          const tile = t as Phaser.Physics.Arcade.Sprite;
          return Math.abs(tile.x - px) < 16;
        },
      );
      if (lavaUnder) return true;
      // Sky-gap check: any ground tile within 16px of x at y >= GROUND_Y.
      const groundUnder = (this.ground.getChildren() as Phaser.GameObjects.GameObject[]).some(
        (g) => {
          const t = g as Phaser.Physics.Arcade.Sprite;
          return Math.abs(t.x - px) < 16 && t.y >= GROUND_Y - 1;
        },
      );
      return !groundUnder;
    };

    // Pickup placements have to dodge the sky-tower / ledge geometry, since
    // a star at GROUND_Y-30 directly under (or inside) a ledge at GROUND_Y-30
    // looks pickable but isn't. Reroll the y a few times; if every attempt
    // collides with a ledge tile, give up on that x rather than burying it.
    // True if a star at (px, py) overlaps OR is in the body-shadow of a
    // ledge tile. Tile origin is (0,0), so it spans [t.x, t.x+16] in x and
    // [t.y, t.y+16] in y. The dino's body is ~14px wide and ~24px tall —
    // even if the star is technically off the tile, a star sitting just
    // beside it has the dino's body collide with the tile while jumping
    // up to grab. We pad both axes by half-the-dino-body so the star ends
    // up in genuinely clear airspace.
    const DINO_HALF_W = 8;
    const DINO_HALF_H = 14;
    const ledgeTileAt = (px: number, py: number) =>
      (this.ground.getChildren() as Phaser.GameObjects.GameObject[]).some((g) => {
        const t = g as Phaser.Physics.Arcade.Sprite;
        if (t.y >= GROUND_Y - 8) return false;
        const tileCenterX = t.x + 8;
        const tileCenterY = t.y + 8;
        // Only flag tiles that are AT or ABOVE the star — those block
        // access from below. A tile BELOW the star is a landing pad the
        // dino can use to reach it (e.g. the big star atop a sky tower).
        if (tileCenterY > py + DINO_HALF_H) return false;
        return (
          Math.abs(tileCenterX - px) < 8 + DINO_HALF_W &&
          Math.abs(tileCenterY - py) < 8 + DINO_HALF_H
        );
      });
    const findClearY = (px: number, lo: number, hi: number): number | null => {
      for (let i = 0; i < 6; i++) {
        const y = GROUND_Y - Phaser.Math.Between(lo, hi);
        if (!ledgeTileAt(px, y)) return y;
      }
      return null;
    };

    for (let x = 120; x < WORLD_WIDTH; x += Phaser.Math.Between(80, 220)) {
      if (isOverHazard(x)) continue;
      const y = findClearY(x, 18, reachable);
      if (y === null) continue;
      this.stars.create(x, y, 'star').setOrigin(0.5, 0.5);
    }
    // BIG STARS — sparser, placed near max apex so they require a real
    // jump (or bouncy mushroom on Canopy) to grab. Worth 3 ammo + 25 score.
    for (let x = 800; x < WORLD_WIDTH; x += Phaser.Math.Between(700, 1400)) {
      if (isOverHazard(x)) continue;
      const y = GROUND_Y - Math.max(reachable - 4, 30);
      // Skip if buried inside a sky-tower ledge.
      if (ledgeTileAt(x, y)) continue;
      const bs = this.bigStars
        .create(x, y, 'big-star')
        .setOrigin(0.5, 0.5)
        .setScale(1.2) as Phaser.Physics.Arcade.Sprite;
      // Halo: pulsing ring around the big star to draw the eye.
      const halo = this.add
        .image(x, y, 'big-star')
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.5)
        .setScale(1.6);
      this.tweens.add({
        targets: halo,
        scale: { from: 1.6, to: 2.4 },
        alpha: { from: 0.5, to: 0 },
        duration: 1100,
        yoyo: false,
        repeat: -1,
      });
      // Pin halo to star's position (so off-camera removal cleans both).
      bs.setData('halo', halo);
    }
    // Sparser hearts. Same reachability rules.
    for (let x = 600; x < WORLD_WIDTH; x += Phaser.Math.Between(700, 1100)) {
      if (isOverHazard(x)) continue;
      const y = findClearY(x, 36, reachable);
      if (y === null) continue;
      this.hearts.create(x, y, 'heart').setOrigin(0.5, 0.5);
    }

    // Bob pickups gently.
    this.tweens.add({
      targets: [...this.stars.getChildren(), ...this.hearts.getChildren()],
      y: '-=2',
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Bullets pool.
    this.bullets = this.physics.add.group({ classType: Bullet, runChildUpdate: true, maxSize: 30 });

    // Player. Spawn x is bumped right when touch controls are visible so
    // the dino doesn't hatch INSIDE the on-screen joystick (which sits
    // centered at x=56 with radius 28). On desktop the joystick isn't
    // rendered, so the original 60px spawn keeps the dino flush left.
    const spawnX = isTouchDevice() ? 130 : 60;
    this.dino = new Dino(this, spawnX, GROUND_Y, this.dinoId);
    this.physics.add.collider(this.dino, this.ground);
    this.dino.onShoot = ({ x, y, dir }) => {
      const b = this.bullets.get(x, y) as Bullet | null;
      if (!b) return;
      b.fire(x, y, dir, 700);
      this.spawnMuzzleFx(x, y);
      this.cameras.main.shake(40, 0.002);
    };
    this.dino.onAmmoChange = (n) => this.hud.setAmmo(n);

    // Aliens.
    this.aliens = this.physics.add.group();
    this.physics.add.collider(this.aliens, this.ground);

    // UFOs.
    this.ufos = this.physics.add.group();

    // Player vs alien — stomp from above kills, side/below contact damages.
    this.physics.add.overlap(this.dino, this.aliens, (_d, a) => {
      const alien = a as Alien;
      if (alien.isDead()) return;
      const dinoBody = this.dino.body as Phaser.Physics.Arcade.Body;
      const alienBody = alien.body as Phaser.Physics.Arcade.Body;
      // Stomp: dino is descending and its feet are at or above the alien's
      // head when contact is made. The +10 fudge factor keeps glancing
      // landings counted as stomps rather than damaging side-bumps.
      const isStomping =
        dinoBody.velocity.y > 30 && dinoBody.bottom <= alienBody.top + 10;
      if (isStomping) {
        alien.killByBullet(this.dino.x);
        // Bounce the dino off the alien's head, classic Mario.
        dinoBody.velocity.y = -420;
        this.spawnHitFx(alien.x, alien.y - 6);
        this.scoreStomp(50, alien.x, alien.y - 8);
        Sfx.hit();
      } else {
        this.dino.takeDamage(1, alien.x);
        this.cameras.main.shake(120, 0.004);
        this.hud.setHp(this.dino.hp);
      }
    });

    // Player vs UFO — descending stomp instakills, side-bumps damage you.
    this.physics.add.overlap(this.dino, this.ufos, (_d, u) => {
      const ufo = u as Ufo;
      if (ufo.isDead()) return;
      const dinoBody = this.dino.body as Phaser.Physics.Arcade.Body;
      const ufoBody = ufo.body as Phaser.Physics.Arcade.Body;
      const isStomping =
        dinoBody.velocity.y > 30 && dinoBody.bottom <= ufoBody.top + 12;
      if (isStomping) {
        ufo.killByBullet(this.dino.x);
        dinoBody.velocity.y = -440;
        this.spawnHitFx(ufo.x, ufo.y);
        this.scoreStomp(100, ufo.x, ufo.y - 8);
        Sfx.ufo();
        this.maybeDropPowerUp(ufo.x, ufo.y);
      } else {
        this.dino.takeDamage(1, ufo.x);
        this.cameras.main.shake(140, 0.005);
        this.hud.setHp(this.dino.hp);
      }
    });

    // Bullets vs aliens — first thing the bullet hits dies instantly.
    this.physics.add.overlap(this.bullets, this.aliens, (b, a) => {
      const bullet = b as Bullet;
      const alien = a as Alien;
      if (!bullet.active || alien.isDead()) return;
      alien.killByBullet(bullet.x);
      this.spawnHitFx(bullet.x, bullet.y);
      bullet.kill();
      this.addScore(50);
      Sfx.hit();
    });

    // Bullets vs UFOs.
    this.physics.add.overlap(this.bullets, this.ufos, (b, u) => {
      const bullet = b as Bullet;
      const ufo = u as Ufo;
      if (!bullet.active || ufo.isDead()) return;
      ufo.killByBullet(bullet.x);
      this.spawnHitFx(bullet.x, bullet.y);
      bullet.kill();
      this.addScore(100);
      Sfx.ufo();
      this.maybeDropPowerUp(ufo.x, ufo.y);
    });

    // Power-ups settle on the ground.
    this.physics.add.collider(this.powerUps, this.ground);
    this.physics.add.collider(this.powerUps, this.bounceTiles);

    // Dino picks up a power-up by walking into it.
    this.physics.add.overlap(this.dino, this.powerUps, (_d, p) => {
      const pu = p as PowerUp;
      if (!pu.active) return;
      this.dino.applyPowerUp(pu.kind);
      this.cameras.main.flash(120, 255, 255, 255, true);
      Sfx.heart();
      this.popText(pu.x, pu.y - 8, pu.kind.toUpperCase(), '#ffffff');
      pu.destroy();
    });

    // Boss HP bar (hidden until a boss is active).
    this.bossHpBg = this.add
      .rectangle(GAME_WIDTH / 2, 44, 220, 8, 0x222233, 0.7)
      .setStrokeStyle(1, 0xffffff, 0.6)
      .setScrollFactor(0)
      .setDepth(900)
      .setVisible(false);
    this.bossHpFill = this.add
      .rectangle(GAME_WIDTH / 2 - 109, 44, 218, 6, 0xff3a4a)
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(901)
      .setVisible(false);
    this.bossHpLabel = this.add
      .text(GAME_WIDTH / 2, 32, '', {
        fontFamily: '"VT323", monospace',
        fontSize: '9px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(901)
      .setVisible(false);

    // Player vs spikes.
    this.physics.add.overlap(this.dino, this.spikes, (_d, s) => {
      const spike = s as Phaser.Physics.Arcade.Sprite;
      this.dino.takeDamage(1, spike.x);
      this.cameras.main.shake(120, 0.004);
      this.hud.setHp(this.dino.hp);
    });

    // Player vs lava (Inferno) — instakill.
    this.physics.add.overlap(this.dino, this.lavaTiles, () => {
      if (this.dino.isDead()) return;
      this.dino.hp = 0;
      this.hud.setHp(0);
      this.dino.die();
      this.cameras.main.shake(220, 0.008);
    });

    // Player vs icicles (Glacier) — damage on contact, falling sprites.
    this.physics.add.overlap(this.dino, this.icicles, (_d, ic) => {
      const icicle = ic as Phaser.Physics.Arcade.Sprite;
      if (this.dino.isDead() || !icicle.active) return;
      this.dino.takeDamage(1, icicle.x);
      this.cameras.main.shake(120, 0.004);
      this.hud.setHp(this.dino.hp);
      icicle.destroy();
    });

    // Icicles also break harmlessly when they hit the ground.
    this.physics.add.collider(this.icicles, this.ground, (ic) => {
      const icicle = ic as Phaser.Physics.Arcade.Sprite;
      this.spawnHitFx(icicle.x, icicle.y);
      icicle.destroy();
    });

    // Player vs bouncy platforms (Canopy) — landing on top super-launches.
    this.physics.add.collider(this.dino, this.bounceTiles, (_d, b) => {
      const dinoBody = this.dino.body as Phaser.Physics.Arcade.Body;
      const bounce = b as Phaser.Physics.Arcade.Sprite;
      if (dinoBody.touching.down) {
        dinoBody.velocity.y = -780;
        Sfx.jump();
        this.tweens.add({
          targets: bounce,
          scaleY: { from: 0.55, to: 1 },
          duration: 220,
          ease: 'Back.easeOut',
        });
      }
    });

    // Glacier icicles — periodic spawn ahead of the player.
    if (this.dinoId === 'doux') {
      this.time.addEvent({
        delay: 1800,
        loop: true,
        callback: () => this.spawnIcicleAhead(),
      });
    }

    // Pickups — stars are AMMO + a small bonus score. Hoard them and you bank
    // bigger numbers at game over (+10 each unspent in the GameOver tally).
    // Visual: the star physically flies up to the HUD ammo widget so the
    // "this thing on the ground IS that thing in your gun" link is explicit.
    this.physics.add.overlap(this.dino, this.stars, (_d, s) => {
      const star = s as Phaser.Physics.Arcade.Sprite;
      this.collectStar(star);
    });
    // BIG STARS — same loop, +3 ammo, +25 score, bigger fly animation.
    this.physics.add.overlap(this.dino, this.bigStars, (_d, s) => {
      const star = s as Phaser.Physics.Arcade.Sprite;
      this.collectBigStar(star);
    });
    this.physics.add.overlap(this.dino, this.hearts, (_d, h) => {
      const heart = h as Phaser.Physics.Arcade.Sprite;
      if (this.dino.hp >= this.dino.maxHp) return;
      heart.destroy();
      this.dino.hp = Math.min(this.dino.maxHp, this.dino.hp + 1);
      this.hud.setHp(this.dino.hp);
      this.popText(heart.x, heart.y, '+1', '#ff3a6b');
      Sfx.heart();
    });

    // Camera follows dino, but locks vertically (good for side-scroller feel).
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, GAME_HEIGHT);
    this.cameras.main.startFollow(this.dino, true, 0.15, 0.15);
    this.cameras.main.setDeadzone(60, GAME_HEIGHT);

    // Inputs.
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.shootKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.X);
    // Right Ctrl as an alt jump. Phaser's KeyCodes share `CONTROL` for
    // both left and right Ctrl (legacy keyCode 17), so we listen on
    // window directly for `KeyboardEvent.code === 'ControlRight'` to
    // distinguish. State is stored on the scene + cleared on shutdown.
    this.rCtrlHeld = false;
    this.rCtrlJustPressed = false;
    this.rCtrlListener = (e: KeyboardEvent) => {
      if (e.code === 'ControlRight') {
        if (!this.rCtrlHeld) this.rCtrlJustPressed = true;
        this.rCtrlHeld = true;
      }
    };
    this.rCtrlReleaseListener = (e: KeyboardEvent) => {
      if (e.code === 'ControlRight') this.rCtrlHeld = false;
    };
    window.addEventListener('keydown', this.rCtrlListener);
    window.addEventListener('keyup', this.rCtrlReleaseListener);
    this.touch = new TouchControls(this);

    // Live god-mode toggle: G flips window.__god so the HUD indicator and
    // damage check both react immediately. Useful for hopping between levels.
    this.input.keyboard!.on('keydown-G', () => {
      const w = window as unknown as { __god?: boolean };
      w.__god = !w.__god;
    });

    // Pause / quit overlay.
    //   P     — toggle pause (real comms box shows a Mr Consequence taunt).
    //   Esc   — quit the level immediately (with a quit-taunt comms beat).
    //   Q     — alias for Esc (safety net).
    //
    // Phaser scene-bound keyboard listeners DON'T fire while the scene is
    // paused, so during pause we listen on `window` directly and tear that
    // listener down on resume / quit.
    const overlayObjs: Phaser.GameObjects.GameObject[] = [];
    const showPauseOverlay = () => {
      // PAUSED sits in the lower playfield — keeps it clear of the comms
      // panel up top so the villain's face isn't obscured.
      const pausedText = this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT - 70, 'PAUSED', {
          fontFamily: '"Press Start 2P", "VT323", monospace',
          fontSize: '20px',
          color: '#ffe23a',
          stroke: '#0d0a1a',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(2000);
      // Light dim across gameplay — depth 500 keeps it under the comms (880)
      // and HUD so the villain still pops above the darken layer.
      const dim = this.add
        .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0d0a1a, 0.45)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(500);
      const quitHint = this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT - 16, 'P RESUME   •   ESC / Q QUIT', {
          fontFamily: '"VT323", monospace',
          fontSize: '10px',
          color: '#ffffff',
          stroke: '#0d0a1a',
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(2000)
        .setAlpha(0.85);
      overlayObjs.push(dim, pausedText, quitHint);
      // The villain himself, in his real comms panel.
      this.comms?.sayStatic({ speaker: 'consequence', text: COMMS.pauseTaunt() });
      // Hide the bottom controls hint while paused — it lives at the same
      // y as our P/ESC quit hint and the two overlap into a jumble.
      if (this.controlsHint) this.controlsHint.setVisible(false);
    };
    const clearOverlay = () => {
      for (const o of overlayObjs) o.destroy();
      overlayObjs.length = 0;
      this.comms?.hideStatic();
      // Restore the controls hint if it's still around (its fade-out
      // tween could have destroyed it during the pause; controlsHint is
      // nulled in that case).
      if (this.controlsHint) this.controlsHint.setVisible(true);
    };

    let winKeyHandler: ((e: KeyboardEvent) => void) | null = null;
    const detachWinKeys = () => {
      if (winKeyHandler) {
        window.removeEventListener('keydown', winKeyHandler);
        winKeyHandler = null;
      }
    };
    const pause = () => {
      if (this.scene.isPaused()) return;
      this.scene.pause();
      Music.stop();
      showPauseOverlay();
      // Window listener handles input while the scene is frozen.
      winKeyHandler = (e: KeyboardEvent) => {
        const k = e.key.toLowerCase();
        if (k === 'p') {
          e.preventDefault();
          resume();
        } else if (k === 'escape' || k === 'q') {
          e.preventDefault();
          quit();
        }
      };
      window.addEventListener('keydown', winKeyHandler);
    };
    const resume = () => {
      if (!this.scene.isPaused()) return;
      detachWinKeys();
      clearOverlay();
      this.scene.resume();
      Music.start(this.dinoId);
    };
    const quit = () => {
      detachWinKeys();
      // If we're paused, reuse the visible overlay; otherwise build one.
      if (!overlayObjs.length) {
        showPauseOverlay();
        if (!this.scene.isPaused()) {
          this.scene.pause();
          Music.stop();
        }
      }
      // Replace PAUSED → QUITTING and swap comms to a quit taunt.
      for (const o of overlayObjs) {
        if (o instanceof Phaser.GameObjects.Text) {
          if (o.text === 'PAUSED') o.setText('QUITTING...');
          else if (o.text.startsWith('P RESUME')) o.setVisible(false);
        }
      }
      this.comms?.sayStatic({ speaker: 'consequence', text: COMMS.quitTaunt() });
      // setTimeout (not Phaser timer) because the scene is paused.
      setTimeout(() => {
        clearOverlay();
        this.scene.resume();
        Music.stop();
        this.scene.start('CharacterSelect');
      }, 1600);
    };
    // Always-on listeners (scene's keyboard system, fires while playing).
    this.input.keyboard!.on('keydown-P', pause);
    this.input.keyboard!.on('keydown-ESC', quit);
    this.input.keyboard!.on('keydown-Q', () => {
      // Q only quits when paused (so it doesn't conflict with anything else
      // mid-play; on Esc-driven quits, the player can also use Esc).
      if (this.scene.isPaused()) quit();
    });
    // Tear down the window listener if the scene shuts down while paused.
    this.events.on('shutdown', detachWinKeys);
    this.events.on('destroy', detachWinKeys);

    // HUD.
    this.hud = new Hud(this, this.dinoId, this.mode);
    this.hud.setHp(this.dino.hp);
    this.hud.setScore(this.score);
    this.hud.setWave(this.wave);
    this.hud.setAmmo(this.dino.ammo);

    // Pause / resume on visibility change so the game doesn't run wild.
    this.events.on('shutdown', () => {
      this.cleanup();
      Music.stop();
      this.comms?.shutdown();
    });
    this.events.on('destroy', () => {
      this.cleanup();
      Music.stop();
      this.comms?.shutdown();
    });

    // Music — start the planet's loop. Each level has its own mood.
    Music.start(this.dinoId);

    // Comms box — Mika + Bosco chime in at key beats. Delay the opener
    // 600ms so the wave banner gets center stage first AND so any
    // late-decoding portrait texture is definitely in the cache.
    this.comms = new CommsBox(this);
    // Reset the shuffle decks so the very first run after a refresh / mode
    // switch starts with a fresh shuffle of every line list. Within a run,
    // each line is drawn at most once before the deck reshuffles.
    if (this.mode === 'practice' || (this.mode === 'gauntlet' && this.dinoId === 'doux')) {
      resetCommsDecks();
    }
    if (this.mode === 'gauntlet' && this.dinoId === 'doux') {
      // Gauntlet open: Mika rallies, then Mr Consequence cuts in to taunt the
      // audacity, then Mika or Bosco fires back. Three-line opener so the
      // rivalry is established before the first wave even starts.
      const [villain, comeback] = COMMS.gauntletVillainIntro();
      this.time.delayedCall(600, () =>
        this.comms?.saySequence([COMMS.gauntletStart(), villain, comeback]),
      );
    } else {
      this.time.delayedCall(600, () => this.comms?.say(COMMS.start(this.dinoId)));
    }

    // Wave intro flash.
    this.popBanner(`WAVE ${this.wave}`);

    // Controls hint along the bottom — only on desktop / keyboard players.
    // Touch users have on-screen JUMP/FIRE buttons so they don't need it.
    if (!isTouchDevice()) {
      const hint = this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT - 8, '← → MOVE   ↑/RCTRL JUMP   SPACE/X FIRE   M MUTE', {
          fontFamily: '"VT323", monospace',
          fontSize: '11px',
          color: '#ffffff',
        })
        .setOrigin(0.5, 1)
        .setScrollFactor(0)
        .setDepth(950)
        .setAlpha(0.85);
      this.controlsHint = hint;
      this.tweens.add({
        targets: hint,
        alpha: 0,
        delay: 4500,
        duration: 1200,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          hint.destroy();
          this.controlsHint = null;
        },
      });
    }
  }

  update(time: number, delta: number) {
    if (this.dino.isDead()) {
      // Allow the dino to fall off-screen, then go to game over.
      if (this.dino.y > GAME_HEIGHT + 80) {
        this.endGame();
      }
      return;
    }

    this.touch.beforeUpdate();

    // Compose movement intent from keyboard + touch.
    let moveX = 0;
    if (this.cursors.left?.isDown) moveX -= 1;
    if (this.cursors.right?.isDown) moveX += 1;
    if (this.cursors.left?.isDown && this.cursors.right?.isDown) moveX = 0;
    const ax = this.touch.axisX;
    if (Math.abs(moveX) < 0.01 && Math.abs(ax) > 0.01) moveX = ax;

    // Jump: UP arrow OR Right Ctrl (alt key for right-hand-only jumping)
    // OR touch button. Right Ctrl is window-tracked since Phaser doesn't
    // distinguish left/right modifier keys; we consume rCtrlJustPressed
    // each frame so a hold doesn't repeatedly trigger jumpPressed.
    const rCtrlPressed = this.rCtrlJustPressed;
    this.rCtrlJustPressed = false;
    const jumpPressed =
      Phaser.Input.Keyboard.JustDown(this.cursors.up!) ||
      rCtrlPressed ||
      this.touch.jumpPressed;
    const jumpHeld = !!(this.cursors.up?.isDown || this.rCtrlHeld || this.touch.jumpHeld);
    // Fire: SPACE or X (X kept as alt for one-handed play). Holding
    // either auto-fires; the dino's gun cooldown gates the rate.
    const shootPressed =
      this.cursors.space?.isDown ||
      this.shootKey.isDown ||
      this.touch.kickHeld ||
      this.touch.kickPressed;

    this.dino.drive({ moveX, jumpPressed, jumpHeld, shootPressed });

    // Alien + UFO spawners — paused while a boss is active so the arena
    // doesn't get overrun.
    if (!this.bossActive) {
      this.spawnTimer += delta;
      if (this.spawnTimer >= this.spawnInterval) {
        this.spawnTimer = 0;
        this.spawnAlienAhead();
      }

      this.ufoSpawnTimer += delta;
      if (this.ufoSpawnTimer >= this.ufoSpawnInterval) {
        this.ufoSpawnTimer = 0;
        this.spawnUfoAhead();
      }
    }

    // Arena clamp: keep the dino inside the boss arena while one is active.
    if (this.bossActive) {
      const minX = this.arenaCenterX - this.arenaHalfWidth + 12;
      const maxX = this.arenaCenterX + this.arenaHalfWidth - 12;
      if (this.dino.x < minX) {
        this.dino.x = minX;
        const body = this.dino.body as Phaser.Physics.Arcade.Body;
        if (body.velocity.x < 0) body.velocity.x = 0;
      } else if (this.dino.x > maxX) {
        this.dino.x = maxX;
        const body = this.dino.body as Phaser.Physics.Arcade.Body;
        if (body.velocity.x > 0) body.velocity.x = 0;
      }

      // Update boss HP bar.
      if (this.currentBoss && !this.currentBoss.isDead()) {
        const ratio = Math.max(0, this.currentBoss.hp / this.currentBoss.maxHp);
        this.bossHpFill.width = 218 * ratio;
      }
    }

    // Wave progression — position-driven for active players, with an
    // idle-only time fallback that closes the "park and farm" exploit:
    //   1) Furthest-right position reached (every 600px = next wave).
    //      Backtracking can't undo a wave because we track maxDinoX.
    //   2) IDLE time only. The clock pauses whenever the player moves
    //      horizontally; resumes from where it left off when they stop.
    //      So an active player never sees time-based waves (they hit
    //      position waves first), but a true AFKer accumulates idle and
    //      gets bumped a wave every 25 idle seconds.
    this.sceneElapsedMs += delta;
    const movedThisFrame = Math.abs(this.dino.x - this.prevDinoX) > 0.5;
    this.prevDinoX = this.dino.x;
    if (movedThisFrame || this.bossActive) {
      this.idleMs = 0;
    } else {
      this.idleMs += delta;
    }
    if (!this.bossActive) {
      this.maxDinoX = Math.max(this.maxDinoX, this.dino.x);
    }
    this.waveProgress = this.maxDinoX;
    const posWave = 1 + Math.floor(this.maxDinoX / 600);
    let targetWave = Math.max(this.wave, posWave);
    // Idle-only fallback: once idleMs crosses 25s, bump exactly one wave
    // and reset the counter so the NEXT idle wave needs another 25s.
    // Without the reset this would fire every frame past the threshold.
    if (this.idleMs >= 25000 && !this.bossActive) {
      targetWave = Math.max(targetWave, this.wave + 1);
      this.idleMs = 0;
    }
    if (targetWave > this.wave && !this.bossActive) {
      this.wave = targetWave;
      const baseSpawn = this.dinoId === 'tard' ? 1300 : 1800;
      this.spawnInterval = Math.max(this.dinoId === 'tard' ? 500 : 700, baseSpawn - (this.wave - 1) * 180);
      this.ufoSpawnInterval = Math.max(2000, 5000 - (this.wave - 1) * 300);
      this.hud.setWave(this.wave);
      // Every 5 waves: a boss instead of a wave banner. The boss locks the
      // arena, suppresses normal spawns, and must be killed to advance.
      if (this.wave % 5 === 0 && !this.bossesCleared.has(this.wave)) {
        this.triggerBoss(this.wave);
      } else {
        this.popBanner(`WAVE ${this.wave}`);
        Sfx.wave();
      }
    }

    // Subtle parallax on background.
    this.bgFar.tilePositionX = this.cameras.main.scrollX * 0.3;
    this.bgNear.tilePositionX = this.cameras.main.scrollX * 0.6;

    // Falling off the world = death.
    if (this.dino.y > GAME_HEIGHT + 40 && !this.dino.isDead()) {
      this.dino.die();
    }

    // Magnet: pull stars toward the dino while it's active.
    if (this.dino.hasMagnet()) {
      const dx0 = this.dino.x;
      const dy0 = this.dino.y - 16;
      const pull = (group: Phaser.Physics.Arcade.Group) => {
        (group.getChildren() as Phaser.Physics.Arcade.Sprite[]).forEach((s) => {
          const d = Phaser.Math.Distance.Between(s.x, s.y, dx0, dy0);
          if (d < 110 && d > 2) {
            const k = (1 - d / 110) * 6;
            s.x += ((dx0 - s.x) / d) * k;
            s.y += ((dy0 - s.y) / d) * k;
          }
        });
      };
      pull(this.stars);
      pull(this.bigStars);
    }

    // Despawn enemies that drifted off-screen. Tighter right margin (was
    // 240, now 100) so flipped aliens that wandered away from the player
    // get cleaned up quickly instead of farming the off-screen pool —
    // closes the "stand still and they pile up" exploit the user noticed.
    (this.aliens.getChildren() as Alien[]).forEach((a) => {
      const sx = this.cameras.main.scrollX;
      if (a.x < sx - 80 || a.x > sx + GAME_WIDTH + 100) a.destroy();
    });
    (this.ufos.getChildren() as Ufo[]).forEach((u) => {
      if (u.x < this.cameras.main.scrollX - 80) u.destroy();
    });
    (this.bullets.getChildren() as Bullet[]).forEach((b) => {
      if (!b.active) return;
      if (b.x < this.cameras.main.scrollX - 40 || b.x > this.cameras.main.scrollX + GAME_WIDTH + 40) {
        b.kill();
      }
    });

    void time;
    this.touch.afterUpdate();
  }

  /**
   * Star → HUD fly animation. Spawns a screen-space ghost that arcs from the
   * pickup point to the ammo widget; ammo + score apply on landing so the
   * count visibly bumps as the star "lands in the slot."
   */
  private collectStar(star: Phaser.Physics.Arcade.Sprite) {
    const cam = this.cameras.main;
    const screenX = star.x - cam.scrollX;
    const screenY = star.y - cam.scrollY;
    star.destroy();
    Sfx.star();

    const ghost = this.add
      .image(screenX, screenY, 'star')
      .setScrollFactor(0)
      .setDepth(950)
      .setScale(1.2);

    // Ammo widget lives at (44, 28) in scene coords (matches Hud.ts).
    const targetX = 50;
    const targetY = 28;

    // Two-stage tween: arc out then home in (gives it a satisfying "magnet" feel).
    const arcX = (screenX + targetX) / 2 + Phaser.Math.Between(-30, 30);
    const arcY = Math.min(screenY, targetY) - 30;

    this.tweens.add({
      targets: ghost,
      x: { value: arcX, duration: 140, ease: 'Sine.easeOut' },
      y: { value: arcY, duration: 140, ease: 'Sine.easeOut' },
      onComplete: () => {
        this.tweens.add({
          targets: ghost,
          x: targetX,
          y: targetY,
          scale: 0.8,
          duration: 200,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            ghost.destroy();
            this.dino.addAmmo(1);
            this.addScore(5);
          },
        });
      },
    });

    // First-pickup teaching hint (per-run).
    if (!this.hasShownStarHint) {
      this.hasShownStarHint = true;
      this.showStarHint();
    }
  }

  /** Big-star variant: bigger reward, more dramatic fly-to-HUD. */
  private collectBigStar(star: Phaser.Physics.Arcade.Sprite) {
    const cam = this.cameras.main;
    const screenX = star.x - cam.scrollX;
    const screenY = star.y - cam.scrollY;
    const halo = star.getData('halo') as Phaser.GameObjects.Image | undefined;
    if (halo) halo.destroy();
    star.destroy();
    Sfx.heart(); // bigger / more melodic than star pickup
    this.cameras.main.flash(120, 255, 226, 58, true);

    const ghost = this.add
      .image(screenX, screenY, 'big-star')
      .setScrollFactor(0)
      .setDepth(950)
      .setScale(2.0);
    const arcX = (screenX + 50) / 2 + Phaser.Math.Between(-40, 40);
    const arcY = Math.min(screenY, 28) - 50;
    this.tweens.add({
      targets: ghost,
      x: { value: arcX, duration: 200, ease: 'Sine.easeOut' },
      y: { value: arcY, duration: 200, ease: 'Sine.easeOut' },
      onComplete: () => {
        this.tweens.add({
          targets: ghost,
          x: 50,
          y: 28,
          scale: 0.9,
          duration: 240,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            ghost.destroy();
            this.dino.addAmmo(3);
            this.addScore(25);
            this.popText(50, 40, '+3', '#ffe23a');
          },
        });
      },
    });
  }

  /** One-shot teaching pop above the dino: "STARS = AMMO" */
  private showStarHint() {
    const t = this.add
      .text(this.dino.x, this.dino.y - 50, 'STARS = AMMO', {
        fontFamily: '"VT323", monospace',
        fontSize: '9px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1)
      .setDepth(950);
    this.tweens.add({
      targets: t,
      y: t.y - 12,
      alpha: { from: 1, to: 0 },
      duration: 1800,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  // ---------------- TERRAIN ----------------

  /**
   * Per-planet world generation. Each dino's stage looks and plays distinctly:
   *  Doux/Glacier  — flat tutorial layout, occasional ledges, sparse spikes.
   *  Mort/Inferno  — gaps in ground filled with instakill lava.
   *  Tard/Dunes    — dense stacked-ledge platforming.
   *  Vita/Canopy   — floating ground islands with sky gaps and high air platforms.
   */
  private buildPlanetTerrain() {
    const groundKey = `ground-${this.dinoId}`;
    switch (this.dinoId) {
      case 'mort':
        this.buildInferno(groundKey);
        break;
      case 'tard':
        this.buildDunes(groundKey);
        break;
      case 'vita':
        this.buildCanopy(groundKey);
        break;
      case 'doux':
      default:
        this.buildGlacier(groundKey);
        break;
    }
  }

  private addGroundTile(x: number, y: number, key: string) {
    this.ground.create(x, y, key).setOrigin(0, 0).refreshBody();
  }

  private addLedge(startX: number, y: number, lengthInTiles: number, key: string) {
    for (let i = 0; i < lengthInTiles; i++) {
      this.addGroundTile(startX + i * TILE, y, key);
    }
  }

  /**
   * A tall multi-layer "tower" of stepped ledges climbing toward the sky.
   * Each layer is 1 tile narrower than the one below for a ziggurat look,
   * with the top platform holding a single big star reward.
   */
  private addSkyTower(baseX: number, key: string, layers = 4) {
    const baseLayerY = GROUND_Y - 30;
    const layerHeight = 30;
    let layerLen = 5;
    for (let i = 0; i < layers; i++) {
      const ly = baseLayerY - i * layerHeight;
      // Step the ledge slightly to the right each layer for variety.
      const offset = i * TILE;
      this.addLedge(baseX + offset, ly, Math.max(2, layerLen), key);
      layerLen -= 1;
    }
    // Reward at the top.
    const topY = baseLayerY - (layers - 1) * layerHeight - 10;
    const topX = baseX + (layers - 1) * TILE + TILE;
    const bs = this.bigStars
      .create(topX, topY, 'big-star')
      .setOrigin(0.5, 0.5)
      .setScale(1.4) as Phaser.Physics.Arcade.Sprite;
    const halo = this.add
      .image(topX, topY, 'big-star')
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.5)
      .setScale(2);
    this.tweens.add({
      targets: halo,
      scale: { from: 2, to: 3 },
      alpha: { from: 0.5, to: 0 },
      duration: 1100,
      repeat: -1,
    });
    bs.setData('halo', halo);
  }

  /**
   * Bouncy mushroom helper — used in tower bases to launch you up. Bails
   * if a ledge sits within ~160px above the spot, which would just bonk
   * the dino's head and steal the bounce. 160px covers Vita's full
   * bounce-launch arc (her apex is 96 and the bounce mushroom multiplies
   * that) — anything closer is a guaranteed head-bonk.
   * Returns true on placement, false if the spot was blocked — callers
   * that loop searching for a clear spot should check the return value.
   */
  private addBouncePadAt(x: number, y: number): boolean {
    if (this.hasLedgeAbove(x, 160)) return false;
    this.bounceTiles
      .create(x, y, 'bounce')
      .setOrigin(0, 0)
      .refreshBody();
    return true;
  }

  /**
   * True when there's a regular (non-lava) ground tile directly under the
   * given x coordinate. Used before placing bouncy mushrooms so they don't
   * float over a sky gap (Canopy) or a lava pit (Inferno).
   */
  private isGroundAt(x: number): boolean {
    const groundUnder = (this.ground.getChildren() as Phaser.GameObjects.GameObject[]).some(
      (g) => {
        const t = g as Phaser.Physics.Arcade.Sprite;
        return Math.abs(t.x - x) < 16 && t.y >= GROUND_Y - 1;
      },
    );
    if (!groundUnder) return false;
    const lavaUnder = (this.lavaTiles.getChildren() as Phaser.GameObjects.GameObject[]).some(
      (t) => Math.abs((t as Phaser.Physics.Arcade.Sprite).x - x) < 16,
    );
    return !lavaUnder;
  }

  /**
   * True when there's a ledge tile (mid-air ground tile, not the actual
   * ground line) within `withinPx` above the given x. Used to:
   *   - Skip pickup placements that would be buried inside a ledge / sky
   *     tower, leaving stars visible but impossible to grab.
   *   - Skip spike placements where a ledge directly above gives the player
   *     a free overpass — the spike never gets a chance to do its job.
   *   - Skip bouncy mushroom placements that would launch the dino's head
   *     into a low ceiling.
   *
   * Horizontal range of 24px catches ledge tiles whose centers are up to
   * one full tile (16px) plus a half-tile margin (8px) offset from the
   * placement — so even a bouncy off the corner of a ledge gets blocked.
   */
  private hasLedgeAbove(x: number, withinPx: number): boolean {
    return (this.ground.getChildren() as Phaser.GameObjects.GameObject[]).some(
      (g) => {
        const t = g as Phaser.Physics.Arcade.Sprite;
        if (t.y >= GROUND_Y - 8) return false; // skip the ground line itself
        return Math.abs(t.x - x) < 24 && t.y > GROUND_Y - withinPx;
      },
    );
  }

  private addSpike(x: number) {
    // Don't place a spike if a ledge directly above it lets the player
    // skip-step over without ever coming near it. ~36px = a comfortable
    // walking-overpass height (just above the dino's standing head).
    if (this.hasLedgeAbove(x, 36)) return;
    this.spikes.create(x, GROUND_Y - 6, 'spike').setOrigin(0, 0).refreshBody();
  }

  /**
   * Prince-of-Persia-style spike trap: hidden flush to the ground, jabs out
   * on a timed cycle. Tard's signature hazard. Deals damage only while up.
   * A soft "click" plays 200ms before extending so the player can react.
   */
  private addJumpingSpike(x: number) {
    const spike = this.spikes
      .create(x, GROUND_Y - 6, 'spike')
      .setOrigin(0, 0)
      .refreshBody() as Phaser.Physics.Arcade.Sprite;
    const body = spike.body as Phaser.Physics.Arcade.StaticBody;
    spike.setTint(0xfff0a0);
    const setActive = (active: boolean) => {
      spike.setVisible(active);
      body.checkCollision.none = !active;
    };
    // Tiny warning marker glows yellow just before the spike pops.
    const warning = this.add
      .rectangle(x + 6, GROUND_Y - 1, 12, 1, 0xffe23a, 0)
      .setOrigin(0, 0);
    setActive(false);
    const initialDelay = Phaser.Math.Between(0, 1800);
    const cycleMs = 2000;
    const upMs = 800;
    const warnLeadMs = 200;
    const tick = () => {
      if (!spike.scene) return;
      // Pre-warning: brief click + visual pulse — but ONLY play the click
      // when the spike is near the camera. Dunes has ~20-30 jumping spikes
      // scattered across the world, all on independent 2-second cycles.
      // Without this gate the player hears 10-15 clicks per second from
      // off-screen spikes, which sounds exactly like morse code (it's
      // what the user kept reporting as "annoying high pitch sound").
      const sx = this.cameras.main.scrollX;
      const onScreen = spike.x > sx - 40 && spike.x < sx + GAME_WIDTH + 40;
      if (onScreen) Sfx.spikeClick();
      this.tweens.add({
        targets: warning,
        alpha: { from: 0, to: 1 },
        duration: warnLeadMs,
        yoyo: false,
      });
      this.time.delayedCall(warnLeadMs, () => {
        if (!spike.scene) return;
        warning.setAlpha(0);
        setActive(true);
        this.time.delayedCall(upMs, () => {
          if (!spike.scene) return;
          setActive(false);
          this.time.delayedCall(cycleMs - upMs - warnLeadMs, tick);
        });
      });
    };
    this.time.delayedCall(initialDelay, tick);
  }

  /**
   * Bouncy mushroom platform: replaces a standard ledge tile. Landing on top
   * launches the dino with extra Y velocity. Vita's signature.
   *
   * Skips placement if a ledge sits within ~160px above — that covers
   * Vita's full mushroom-launch arc, so we never plant a spring under a
   * stack of platforms (the dino would just bonk its head into the
   * underside of the lowest one and the mushroom would feel broken).
   */
  private addBouncePlatform(x: number, y: number) {
    if (this.hasLedgeAbove(x, 160)) return;
    const pad = this.bounceTiles
      .create(x, y, 'bounce')
      .setOrigin(0, 0)
      .refreshBody() as Phaser.Physics.Arcade.Sprite;
    void pad;
  }

  /**
   * Falling icicle hazard — drops from the top of the screen at a random x
   * ahead of the player. Damages on contact with dino, breaks on ground.
   * Glacier's signature.
   */
  private spawnIcicleAhead() {
    if (this.dino.isDead() || this.bossActive) return;
    const camRight = this.cameras.main.scrollX + GAME_WIDTH;
    const x = this.cameras.main.scrollX + Phaser.Math.Between(40, GAME_WIDTH - 40);
    if (x > WORLD_WIDTH - 30) return;
    const ic = this.icicles.create(x, -10, 'icicle') as Phaser.Physics.Arcade.Sprite;
    ic.setOrigin(0.5, 0).setScale(2);
    const body = ic.body as Phaser.Physics.Arcade.Body;
    body.setSize(4, 12);
    body.setOffset(1, 0);
    body.setAllowGravity(true);
    body.setVelocityY(180);
    // Brief warning shadow on the ground so the player has a chance to dodge.
    const shadow = this.add
      .ellipse(x, GROUND_Y - 2, 16, 5, 0xffffff, 0.5)
      .setStrokeStyle(1, 0xffffff, 0.8);
    this.tweens.add({
      targets: shadow,
      alpha: { from: 0.8, to: 0.2 },
      scaleX: { from: 1, to: 1.4 },
      duration: 500,
      yoyo: true,
      repeat: 1,
      onComplete: () => shadow.destroy(),
    });
    void camRight;
  }

  private buildGlacier(gk: string) {
    // Continuous ground, gentle ledges, sparse spikes — tutorial energy.
    for (let x = 0; x < WORLD_WIDTH; x += TILE) {
      this.addGroundTile(x, GROUND_Y, gk);
      if (Phaser.Math.Between(0, 100) < 8 && x > 200) {
        const ly = GROUND_Y - Phaser.Math.Between(30, 50);
        this.addLedge(x, ly, Phaser.Math.Between(2, 4), gk);
      }
    }
    // Sky towers — every ~1500-2000px, 4 layers high with a big star on top.
    for (let x = 900; x < WORLD_WIDTH - 200; x += Phaser.Math.Between(1500, 2200)) {
      this.addSkyTower(x, gk, 4);
    }
    for (let x = 320; x < WORLD_WIDTH - 200; x += Phaser.Math.Between(220, 420)) {
      this.addSpike(x);
    }
  }

  private buildInferno(gk: string) {
    // Lava pits punctuate the ground every few hundred px. Falling in = death.
    // Mort/Roxy's running-jump horizontal range is ~107px; a single 4-tile
    // pit (64px) is well within that, but TWO back-to-back 4-tile pits
    // would be 128px and impossible. `lastWasPit` forces solid ground after
    // every pit so the maximum jump distance stays at 4 tiles. We also cap
    // pitLen at 3 (48px) — that's the worst case the player ever has to
    // clear from a flat-footed jump approach (pre-running speed).
    let x = 0;
    let lastWasPit = false;
    while (x < WORLD_WIDTH) {
      const canPit = x > 240 && x < WORLD_WIDTH - 100;
      const startPit = canPit && !lastWasPit && Phaser.Math.Between(0, 100) < 14;
      if (startPit) {
        const pitLen = Phaser.Math.Between(2, 3);
        for (let i = 0; i < pitLen; i++) {
          this.lavaTiles
            .create(x + i * TILE, GROUND_Y + 4, 'lava')
            .setOrigin(0, 0)
            .refreshBody();
        }
        x += pitLen * TILE;
        lastWasPit = true;
      } else {
        this.addGroundTile(x, GROUND_Y, gk);
        if (Phaser.Math.Between(0, 100) < 6 && x > 250) {
          const ly = GROUND_Y - Phaser.Math.Between(34, 52);
          this.addLedge(x, ly, Phaser.Math.Between(2, 3), gk);
        }
        x += TILE;
        lastWasPit = false;
      }
    }
    // Sky towers — even Mort's brutal level rewards going UP. A bouncy
    // pad sits at the base so Mort (lowest jumper) can also reach the top.
    // Search a small window around xt-2 tiles for any solid (non-lava)
    // ground; if everything nearby is a pit, skip the pad rather than
    // floating it over lava.
    for (let xt = 900; xt < WORLD_WIDTH - 200; xt += Phaser.Math.Between(1600, 2400)) {
      this.addSkyTower(xt, gk, 4);
      // Find a spot near the tower where there's solid ground AND no ledge
      // overhead within bounce range. Without the ceiling check the pad
      // would slot right under the tower's lowest layer and steal Mort's
      // launch — exactly the bug the user reported.
      let placed = false;
      for (let off = 0; off <= TILE * 4 && !placed; off += TILE) {
        for (const dir of [-1, 1]) {
          const px = xt + dir * (TILE * 2 + off);
          if (this.isGroundAt(px) && this.addBouncePadAt(px, GROUND_Y - 8)) {
            placed = true;
            break;
          }
        }
      }
    }
    // Inferno's signature hazard is the lava itself — spikes here are
    // redundant (you'd die on the lava before the spike mattered) and
    // visually confusing on a level that's already wall-to-wall danger.
    // Each level now has exactly one signature hazard:
    //   Glacier — falling icicles
    //   Inferno — lava pits (no spikes)
    //   Dunes   — jumping spikes
    //   Canopy  — sky gaps + bouncy mushrooms
  }

  private buildDunes(gk: string) {
    // Stacked-ledge platforming. Tard is the slowest jumper of the bunch
    // (apex 69px, horizontal range ~78px), so we cap stacks at 2 layers and
    // align widths so each layer is reachable from below.
    let lastStackEnd = 220;
    for (let x = 0; x < WORLD_WIDTH; x += TILE) {
      this.addGroundTile(x, GROUND_Y, gk);
      if (
        Phaser.Math.Between(0, 100) < 16 &&
        x > 220 &&
        x - lastStackEnd >= 64 // keep stacks reasonably spaced
      ) {
        const stackHeight = Phaser.Math.Between(1, 2);
        const baseLen = Phaser.Math.Between(3, 5);
        for (let layer = 0; layer < stackHeight; layer++) {
          // Each layer 26px above the previous; max layer-1 top = 196px,
          // well within Tard's apex of 177 from layer-0 top (214).
          const ly = GROUND_Y - 32 - layer * 26;
          // Make upper layers AT LEAST as wide as lower so the dino can
          // always reach the upper layer from any lower layer position.
          const len = Math.max(2, baseLen - layer);
          this.addLedge(x, ly, len, gk);
        }
        lastStackEnd = x + baseLen * TILE;
      }
    }
    // Denser hazard cadence — Tard's slower dino still has time to react,
    // but the level should never feel "quiet" the way the user noted.
    // Sky towers — Tard's planet plays well with vertical (lots of stack
    // navigation already). Place fewer because the level is already busy.
    for (let xt = 1100; xt < WORLD_WIDTH - 200; xt += Phaser.Math.Between(1800, 2600)) {
      this.addSkyTower(xt, gk, 4);
    }
    for (let x = 240; x < WORLD_WIDTH - 200; x += Phaser.Math.Between(110, 200)) {
      // 50% of spike traps in Dunes are Prince-of-Persia jumping spikes.
      if (Phaser.Math.Between(0, 100) < 50) {
        this.addJumpingSpike(x);
      } else {
        this.addSpike(x);
      }
    }
  }

  private buildCanopy(gk: string) {
    // Floating ground islands with sky gaps + high air platforms.
    let x = 0;
    // Safe starting strip so the dino doesn't fall instantly.
    while (x < 200) {
      this.addGroundTile(x, GROUND_Y, gk);
      x += TILE;
    }
    // `lastWasGap` prevents two gaps in a row. Vita's max jump range from
    // a running start is ~117px (jump apex 96 / time 0.42s × speed 140);
    // a single 4-tile gap is 64px which she clears with margin, but two
    // consecutive 4-tile gaps would be 128px = unreachable. After a gap
    // we always force the next iteration to lay an island.
    let lastWasGap = false;
    while (x < WORLD_WIDTH) {
      const wantIsland = lastWasGap || Phaser.Math.Between(0, 100) < 65;
      if (wantIsland) {
        const len = Phaser.Math.Between(4, 9);
        for (let i = 0; i < len && x < WORLD_WIDTH; i++) {
          this.addGroundTile(x, GROUND_Y, gk);
          x += TILE;
        }
        lastWasGap = false;
      } else {
        // Sky gap — falling through it = death. Cap at 3 tiles so even
        // Vita's worst-case stationary jump can clear it; running jumps
        // handle 4 tiles fine but stationary nudges (e.g. landing flat
        // on the last tile then jumping) are tighter.
        const gapLen = Phaser.Math.Between(2, 3);
        x += gapLen * TILE;
        lastWasGap = true;
      }
      // Sometimes a high air platform above the island.
      if (Phaser.Math.Between(0, 100) < 35) {
        const ly = GROUND_Y - Phaser.Math.Between(48, 78);
        const len = Phaser.Math.Between(2, 3);
        this.addLedge(x - len * TILE, ly, len, gk);
      }
    }
    // Bouncy mushroom pads — sit ON the ground (y = GROUND_Y - 8 means the
    // tile occupies y=238-246, top edge flush with the ground surface).
    // Skip placement if the x-position is over a sky gap or hazard.
    for (let bx = 360; bx < WORLD_WIDTH - 200; bx += Phaser.Math.Between(280, 560)) {
      if (!this.isGroundAt(bx)) continue;
      this.addBouncePlatform(bx, GROUND_Y - 8);
    }
    // Sky towers + a sky-bonus cluster (3 big stars up REAL high). On
    // Canopy specifically, the world is full of sky gaps, so we have to
    // make sure each tower's base column sits over solid ground —
    // otherwise the tower's lowest ledge is floating over an abyss the
    // dino can't approach from. Walk a few tiles in each direction
    // looking for a ground tile and nudge the tower onto it.
    for (let xt = 800; xt < WORLD_WIDTH - 200; xt += Phaser.Math.Between(1400, 2200)) {
      let towerX = xt;
      if (!this.isGroundAt(towerX)) {
        let found = false;
        for (let off = TILE; off <= TILE * 6 && !found; off += TILE) {
          for (const dir of [-1, 1]) {
            if (this.isGroundAt(xt + dir * off)) {
              towerX = xt + dir * off;
              found = true;
              break;
            }
          }
        }
        if (!found) continue; // genuinely no ground nearby — skip this tower
      }
      this.addSkyTower(towerX, gk, 5);
    }
    // Sky bonus chamber — small island of 3 big stars at near-top of screen.
    // Vita's super-jump from a bouncy pad makes this reachable.
    for (let bx = 1500; bx < WORLD_WIDTH - 600; bx += 2800) {
      const cy = 60;
      // Place 3 big stars in a row.
      for (let i = -1; i <= 1; i++) {
        const sx = bx + i * 30;
        const bs = this.bigStars
          .create(sx, cy, 'big-star')
          .setOrigin(0.5, 0.5)
          .setScale(1.5) as Phaser.Physics.Arcade.Sprite;
        const halo = this.add
          .image(sx, cy, 'big-star')
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(0.6)
          .setScale(2.2);
        this.tweens.add({
          targets: halo,
          scale: { from: 2.2, to: 3.4 },
          alpha: { from: 0.6, to: 0 },
          duration: 1200,
          delay: i * 200,
          repeat: -1,
        });
        bs.setData('halo', halo);
      }
      // Bouncy mushroom underneath to launch up. Sit it ON the ground;
      // skip if the spot is a sky gap (Canopy) so it doesn't float.
      if (this.isGroundAt(bx - 8)) {
        this.addBouncePadAt(bx - 8, GROUND_Y - 8);
      } else {
        // Fall back to the nearest ground tile within ±64px.
        for (let off = 16; off <= 64; off += 16) {
          if (this.isGroundAt(bx - 8 + off)) {
            this.addBouncePadAt(bx - 8 + off, GROUND_Y - 8);
            break;
          }
          if (this.isGroundAt(bx - 8 - off)) {
            this.addBouncePadAt(bx - 8 - off, GROUND_Y - 8);
            break;
          }
        }
      }
    }
    for (let xs = 480; xs < WORLD_WIDTH - 200; xs += Phaser.Math.Between(600, 1100)) {
      this.addSpike(xs);
    }
  }

  // ---------------- BOSS ----------------

  /** WARNING-style cinematic flash before the boss spawns. */
  private runBossIntro(wave: number) {
    const cam = this.cameras.main;
    const isFinal = wave >= 25;
    cam.shake(280, 0.005);
    // Mika/Bosco shouts a warning, then Mr Consequence — the villain
    // mastermind — cuts in to taunt us with a planet-flavored line.
    this.comms?.saySequence([COMMS.bossIncoming(), COMMS.villainTaunt(this.dinoId)]);
    // Red overlay flash.
    const overlay = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0xff0a14, 0.55)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(940);
    this.tweens.add({
      targets: overlay,
      alpha: { from: 0.55, to: 0 },
      duration: 900,
      ease: 'Cubic.easeIn',
      onComplete: () => overlay.destroy(),
    });
    // Big WARNING text.
    const warn = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, isFinal ? '!! FINAL BOSS !!' : '!! WARNING !!', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '20px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#ff0a14',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(945)
      .setAlpha(0);
    this.tweens.add({
      targets: warn,
      alpha: { from: 0, to: 1 },
      scale: { from: 1.6, to: 1 },
      duration: 220,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: warn,
      alpha: 0,
      delay: 700,
      duration: 280,
      ease: 'Cubic.easeIn',
      onComplete: () => warn.destroy(),
    });
    // Heavy bossEnter SFX.
    Sfx.bossEnter();
  }


  private triggerBoss(wave: number) {
    if (this.bossActive) return;
    this.bossActive = true;
    const cam = this.cameras.main;

    // Lock camera at current scroll position.
    cam.stopFollow();
    this.arenaCenterX = cam.scrollX + GAME_WIDTH / 2;

    // Boss intro cinematic — flash a red WARNING banner, tint the screen,
    // shake briefly, THEN drop the boss in. Sells the boss as an event.
    this.runBossIntro(wave);

    // Wave 25 = final boss with 2× HP.
    const isFinal = wave >= 25;
    const hp = isFinal ? 12 : 6;
    const spawnX = this.arenaCenterX + 80;
    const spawnY = -40; // drops in from above
    const boss = new BossAsteroid(this, spawnX, spawnY, hp, this.dinoId);
    boss.onSpitMinion = (alien) => {
      this.aliens.add(alien);
    };
    boss.onDropBomb = (bx, by) => {
      // Mort's lava bombs — falling sprite that damages on contact.
      const bomb = this.physics.add.sprite(bx, by, 'star').setTint(0xff3a1a).setScale(2.5);
      const body = bomb.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(true);
      body.setVelocityY(60);
      body.setSize(8, 8);
      // Spinny, glowy, ominous.
      this.tweens.add({
        targets: bomb,
        angle: 360,
        duration: 600,
        repeat: -1,
      });
      // Damage dino on overlap; explode on ground impact.
      this.physics.add.overlap(this.dino, bomb, () => {
        if (this.dino.isDead() || !bomb.active) return;
        this.dino.takeDamage(1, bomb.x);
        this.cameras.main.shake(140, 0.005);
        this.hud.setHp(this.dino.hp);
        this.spawnHitFx(bomb.x, bomb.y);
        bomb.destroy();
      });
      this.physics.add.collider(bomb, this.ground, () => {
        if (!bomb.active) return;
        this.spawnHitFx(bomb.x, bomb.y);
        this.cameras.main.shake(80, 0.002);
        bomb.destroy();
      });
      // Auto-clean if it falls off-screen.
      this.time.delayedCall(4500, () => bomb.active && bomb.destroy());
    };
    boss.onDefeated = () => this.onBossDefeated(wave, isFinal);
    this.currentBoss = boss;
    // Patrol band: ~70px above ground. Low enough that even a Doux apex
    // shot has comfortable vertical clearance, high enough that you can't
    // simply walk under it.
    (boss as unknown as { bobBaseY: number }).bobBaseY = GROUND_Y - 75;

    // Boss vs bullets: each hit is exactly 1 damage.
    // NOTE: Phaser 3.90 routes Group-vs-Sprite overlap through
    // collideSpriteVsGroup, which calls our callback with (sprite, group_member)
    // — i.e. (boss, bullet) — not the order add.overlap() arguments suggest.
    // We pick the bullet by identity rather than position.
    this.physics.add.overlap(this.bullets, boss, (a, c) => {
      const bullet = (a === boss ? c : a) as Bullet;
      if (!bullet || !bullet.active || boss.isDead()) return;
      boss.takeBulletHit(bullet.x);
      this.spawnHitFx(bullet.x, bullet.y);
      bullet.kill();
      Sfx.hit();
    });

    // Boss vs dino — descending stomp deals damage WITHOUT hurting you,
    // any other contact damages you. Stomp does the same as one bullet,
    // with no ammo cost — high-risk close-range option.
    this.physics.add.overlap(this.dino, boss, () => {
      if (boss.isDead()) return;
      const dinoBody = this.dino.body as Phaser.Physics.Arcade.Body;
      const bossBody = boss.body as Phaser.Physics.Arcade.Body;
      const isStomping =
        dinoBody.velocity.y > 30 && dinoBody.bottom <= bossBody.top + 16;
      if (isStomping) {
        boss.takeStomp();
        dinoBody.velocity.y = -460;
        this.spawnHitFx(this.dino.x, dinoBody.bottom);
        Sfx.hit();
      } else {
        this.dino.takeDamage(1, boss.x);
        this.cameras.main.shake(160, 0.006);
        this.hud.setHp(this.dino.hp);
      }
    });

    // Mort's Magma Orb hovers too high to stomp from ground level — give
    // the player two elevated platforms inside the arena so they can climb
    // up and head-bounce the boss the same way every other planet allows.
    if (this.dinoId === 'mort') {
      const platformY = GROUND_Y - 50;
      const groundKey = `ground-${this.dinoId}`;
      for (const cx of [this.arenaCenterX - 80, this.arenaCenterX + 80]) {
        this.addLedge(cx - TILE, platformY, 3, groundKey);
      }
    }

    // Show HP bar + boss banner.
    this.bossHpBg.setVisible(true);
    this.bossHpFill.setVisible(true);
    this.bossHpFill.width = 218;
    this.bossHpLabel.setVisible(true).setText(isFinal ? 'FINAL BOSS' : `BOSS  WAVE ${wave}`);
  }

  private onBossDefeated(_wave: number, _isFinalWave: boolean) {
    this.bossActive = false;
    this.currentBoss = null;
    this.bossHpBg.setVisible(false);
    this.bossHpFill.setVisible(false);
    this.bossHpLabel.setVisible(false);
    // Big score reward — gauntlet's final boss pays out the big chunk.
    const idx = DINO_ORDER.indexOf(this.dinoId);
    const isGauntletFinal = this.mode === 'gauntlet' && idx === DINO_ORDER.length - 1;
    this.addScore(isGauntletFinal ? 1000 : 200);
    // Big satisfying boss-death rumble (longer + heavier than regular hits).
    this.cameras.main.shake(isGauntletFinal ? 900 : 600, 0.02);
    this.cameras.main.flash(180, 255, 226, 58);
    Sfx.victory();

    // Branch on mode + position.
    // (Boss-defeated chatter shown on the cleared/handoff screens — the
    // 900ms scene transition here doesn't leave room for it to land.)

    if (this.mode === 'practice') {
      // Single-level run: show the planet-cleared screen.
      this.time.delayedCall(900, () => {
        this.scene.start('LevelCleared', {
          dinoId: this.dinoId,
          score: this.score,
        });
      });
    } else if (isGauntletFinal) {
      // Last gauntlet segment: full PLANET CLEARED victory scene + finale
      // animation + leaderboard form. Pass the runStartMs through so the
      // finale can compute total run duration.
      this.time.delayedCall(900, () => {
        this.scene.start('PlanetCleared', {
          score: this.score,
          dinoId: this.dinoId,
          runStartMs: this.runStartMs,
        });
      });
    } else {
      // Mid-gauntlet: rocket handoff to the next dino.
      const nextDinoId = DINO_ORDER[idx + 1];
      this.time.delayedCall(900, () => {
        this.scene.start('Handoff', {
          prevDinoId: this.dinoId,
          nextDinoId,
          runScore: this.score,
          runStartMs: this.runStartMs,
        });
      });
    }
  }

  // ---------------- spawners ----------------

  /**
   * Per-planet enemy palette so aliens and UFOs visually belong to the
   * planet they show up on (frosty, charred, sandy, leafy).
   */
  private enemyTints(): { alien: number; ufo: number } {
    const map: Record<DinoId, { alien: number; ufo: number }> = {
      doux: { alien: 0xafe5ff, ufo: 0xc0e0ff },
      mort: { alien: 0xff8c4a, ufo: 0xff6638 },
      tard: { alien: 0xd9b886, ufo: 0xe0c898 },
      vita: { alien: 0x6cd97a, ufo: 0x9affae },
    };
    return map[this.dinoId];
  }

  /** UFO kill drop chance. ~15% odds, uniform across the four types. */
  private maybeDropPowerUp(x: number, y: number) {
    if (Phaser.Math.Between(0, 100) >= 15) return;
    const kinds: PowerUpKind[] = ['rapid', 'shield', 'magnet', 'triple'];
    const kind = kinds[Phaser.Math.Between(0, kinds.length - 1)];
    const pu = new PowerUp(this, x, y, kind);
    this.powerUps.add(pu);
  }

  private spawnAlienAhead() {
    if (this.dino.isDead()) return;
    // Concurrent-alien cap. Above ~6 alive at once the screen is already
    // a hazard; spawning more just rewards standing-still farming. We
    // count only aliens within reach of the camera so off-world stragglers
    // (despawning) don't keep the spawner muzzled.
    const sx = this.cameras.main.scrollX;
    const onScreenAliens = (this.aliens.getChildren() as Alien[]).filter(
      (a) => a.x > sx - 80 && a.x < sx + GAME_WIDTH + 100,
    ).length;
    if (onScreenAliens >= 6) return;
    const camRight = sx + GAME_WIDTH;
    const x = camRight + Phaser.Math.Between(20, 60);
    if (x > WORLD_WIDTH - 50) return;
    // Each planet has its own enemy sprite (see PreloadScene). The tint is
    // a small extra polish on top of the planet-themed art.
    const a = new Alien(this, x, GROUND_Y - 1, undefined, `alien-${this.dinoId}`);
    a.walkDir = -1;
    this.aliens.add(a);
  }

  private spawnUfoAhead() {
    if (this.dino.isDead()) return;
    const camRight = this.cameras.main.scrollX + GAME_WIDTH;
    const x = camRight + Phaser.Math.Between(40, 100);
    if (x > WORLD_WIDTH - 50) return;
    // Cruise high up, dive into mid-air bullet range.
    const cruiseY = Phaser.Math.Between(60, 100);
    const diveY = Phaser.Math.Between(GROUND_Y - 90, GROUND_Y - 60);
    const u = new Ufo(this, x, cruiseY, diveY, this.enemyTints().ufo);
    this.ufos.add(u);
  }

  private spawnMuzzleFx(x: number, y: number) {
    const fx = this.add.image(x, y, 'muzzle-fx').setDepth(800).setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: fx,
      scale: { from: 1, to: 1.8 },
      alpha: { from: 1, to: 0 },
      duration: 100,
      ease: 'Cubic.easeOut',
      onComplete: () => fx.destroy(),
    });
  }

  private spawnHitFx(x: number, y: number) {
    const fx = this.add.image(x, y, 'muzzle-fx').setDepth(800).setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: fx,
      scale: { from: 0.8, to: 2.2 },
      alpha: { from: 1, to: 0 },
      duration: 200,
      ease: 'Cubic.easeOut',
      onComplete: () => fx.destroy(),
    });
  }

  private addScore(n: number) {
    this.score += n;
    this.hud.setScore(this.score);
    // Random Mika/Bosco encouragement at every 500-score milestone.
    if (this.score >= this.nextEncouragementScore) {
      this.nextEncouragementScore += 500;
      this.comms?.say(COMMS.encouragement());
    }
  }

  /**
   * Score a stomp with combo handling. If a previous stomp landed inside
   * COMBO_WINDOW, the multiplier ratchets up. Pops a "x{n}!" floater for
   * any combo ≥ 2, with bigger text + brighter color as it climbs.
   */
  private scoreStomp(baseScore: number, x: number, y: number) {
    const now = this.time.now;
    if (now - this.comboLastAt < GameScene.COMBO_WINDOW) {
      this.comboCount += 1;
    } else {
      this.comboCount = 1;
    }
    this.comboLastAt = now;
    const multiplied = baseScore * this.comboCount;
    this.addScore(multiplied);
    if (this.comboCount >= 2) {
      const fontSize = Math.min(20, 10 + this.comboCount * 2);
      const color = this.comboCount >= 5 ? '#ff3a6b' : this.comboCount >= 3 ? '#ffe23a' : '#ffffff';
      const t = this.add
        .text(x, y - 14, `x${this.comboCount}!`, {
          fontFamily: '"Press Start 2P", "VT323", monospace',
          fontSize: `${fontSize}px`,
          color,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(960);
      this.tweens.add({
        targets: t,
        y: t.y - 24,
        scale: { from: 1.4, to: 1 },
        alpha: { from: 1, to: 0 },
        duration: 700,
        ease: 'Cubic.easeOut',
        onComplete: () => t.destroy(),
      });
    }
  }

  private popText(x: number, y: number, msg: string, color: string) {
    const t = this.add
      .text(x, y, msg, { fontFamily: '"VT323", monospace', fontSize: '11px', color })
      .setOrigin(0.5)
      .setDepth(900);
    this.tweens.add({
      targets: t,
      y: y - 16,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  private popBanner(text: string) {
    const cam = this.cameras.main;
    const t = this.add
      .text(cam.midPoint.x, GAME_HEIGHT / 2 - 40, text, {
        fontFamily: '"VT323", monospace',
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(950);
    t.setPosition(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40);
    this.tweens.add({
      targets: t,
      alpha: { from: 1, to: 0 },
      y: { from: GAME_HEIGHT / 2 - 40, to: GAME_HEIGHT / 2 - 60 },
      duration: 1100,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  private endGame() {
    this.scene.start('GameOver', {
      score: this.score,
      wave: this.wave,
      dinoId: this.dinoId,
      ammoLeft: this.dino.ammo,
      mode: this.mode,
    });
  }

  private cleanup() {
    this.input.removeAllListeners();
    // Detach the window-level Right-Ctrl listeners so they don't stack
    // (or fire against a destroyed scene) on restart / scene transitions.
    if (this.rCtrlListener) {
      window.removeEventListener('keydown', this.rCtrlListener);
      this.rCtrlListener = null;
    }
    if (this.rCtrlReleaseListener) {
      window.removeEventListener('keyup', this.rCtrlReleaseListener);
      this.rCtrlReleaseListener = null;
    }
    this.rCtrlHeld = false;
    this.rCtrlJustPressed = false;
  }
}
